import { useState } from 'react';
import { CandidateInterviewPageStages } from '@/types';
import { incorrectUrlMessage, technicalValidationProjectSchema } from '@/utils/candidate';
import SubmittingTechnicalValidationComponent, {
  TechnicalDesignType,
} from './SubmittingTechnicalValidation.component';

type SubmittingTechnicalValidationProps = {
  onChangeInterviewState: (state: CandidateInterviewPageStages) => void;
  onSubmitProject: (data: SubmitProjectProps) => void;
};

export type SubmitProjectProps = {
  technicalDesign: TechnicalDesignType;
  technicalVideo: File;
  githubProjectUrl: string;
};

const defaultTechnicalDesignValues = {
  title: '',
  prevTitle: '',
  body: '',
  prevBody: '',
  isOpen: false,
};

export default function SubmittingTechnicalValidation({
  onChangeInterviewState,
  onSubmitProject,
}: SubmittingTechnicalValidationProps): JSX.Element {
  const [githubUrl, setGithubUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [technicalDesign, setTechnicalDesign] = useState<TechnicalDesignType>(
    defaultTechnicalDesignValues
  );
  const [videoFile, setVideoFile] = useState<File | null>(null);

  const onAddGithubUrl = (url: string): void => {
    setGithubUrl(url);
  };
  const onToggleTechnicalDesignModal = (): void => {
    setTechnicalDesign((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  };

  const onSaveTechnicalDesign = (): void => {
    setTechnicalDesign((prev) => ({
      ...prev,
      prevTitle: prev.title,
      prevBody: prev.body,
      isOpen: false,
    }));
  };

  const onChangeTechnicalDesign = (value: string, input: 'title' | 'body'): void => {
    setTechnicalDesign((prev) => ({
      ...prev,
      [input]: value,
    }));
  };

  const onCloseTechnicalDesignModal = (): void => {
    setTechnicalDesign((prev) => ({
      ...prev,
      title: prev.prevTitle,
      body: prev.prevBody,
      isOpen: false,
    }));
  };

  const onRemoveFile = (fileType: 'video' | 'technicalDesign'): void => {
    if (fileType === 'technicalDesign') {
      setTechnicalDesign(defaultTechnicalDesignValues);
      return;
    }

    setVideoFile(null);
  };

  const onAddVideo = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { files } = event.target;
    setVideoFile(files ? files[0] : null);
  };

  const canSubmitProject = !!githubUrl && !!videoFile && !!technicalDesign.title;

  const handleSubmitProject = async (): Promise<void> => {
    const isUrlValid = await technicalValidationProjectSchema.isValid({
      githubUrl,
    });

    if (!isUrlValid) {
      setUrlError(incorrectUrlMessage);
      return;
    }

    if (canSubmitProject) {
      setUrlError('');

      onSubmitProject({
        technicalDesign,
        technicalVideo: videoFile,
        githubProjectUrl: githubUrl,
      });
    }
  };

  return (
    <SubmittingTechnicalValidationComponent
      canSubmitProject={canSubmitProject}
      githubUrl={githubUrl}
      urlError={urlError}
      technicalDesign={technicalDesign}
      videoFile={videoFile}
      onSubmitProject={handleSubmitProject}
      onAddGithubUrl={onAddGithubUrl}
      onToggleTechnicalDesignModal={onToggleTechnicalDesignModal}
      onCloseTechnicalDesignModal={onCloseTechnicalDesignModal}
      onRemoveFile={onRemoveFile}
      onAddVideo={onAddVideo}
      onGoBack={() => onChangeInterviewState(CandidateInterviewPageStages.WELCOME)}
      onSaveTechnicalDesign={onSaveTechnicalDesign}
      onChangeTechnicalDesign={onChangeTechnicalDesign}
    />
  );
}
